import { useStores } from '@littlebird/sdk';
import { useLocation } from '@tanstack/react-router';
import { computed } from 'mobx';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { uiStore } from '@/stores/ui.store';
import { navigate } from '@/utils/navigation-service';

import { createAction } from './actions';
import { getThreadId } from './actions/chat';
import {
  collectRecentCandidates,
  selectRecentItems,
  type RecentCandidate,
} from './recent-items';

import type { Action } from './actions/types';

const RECENT_ICON_SIZE = 14;

function buildRecentActions(items: RecentCandidate[]): Action[] {
  return items.map((item, index) => {
    const date = item.date.toISOString();
    if (item.kind === 'chat') {
      const title = item.title || 'Untitled';
      return createAction({
        id: `recent:chat:${item.id}`,
        name: title,
        section: 'Recent',
        priority: items.length - index,
        icon: <Icons.chat size={RECENT_ICON_SIZE} />,
        date,
        preview: {
          kind: 'chat',
          threadId: item.id,
          title,
          updatedAt: date,
          mentionCount: 0,
          query: '',
        },
        perform: () => {
          navigate({
            to: '/chats/$chatId',
            params: { chatId: item.id },
          });
        },
      });
    }
    return createAction({
      id: `recent:meeting:${item.id}`,
      name: item.title,
      section: 'Recent',
      priority: items.length - index,
      icon: <Icons.meetings size={RECENT_ICON_SIZE} />,
      date,
      preview: {
        kind: 'meeting',
        meetingId: item.id,
        label: 'Meeting summary',
        title: item.title,
        content: item.summary ?? '',
        updatedAt: date,
        query: '',
      },
      perform: () => {
        navigate({
          to: '/meetings/$meetingId',
          params: { meetingId: item.id },
        });
      },
    });
  });
}

const NO_CANDIDATES: RecentCandidate[] = [];
const NO_RECENT_ACTIONS: Action[] = [];

export function useRecentCmdBarActions(visible: boolean): Action[] {
  const stores = useStores();
  const { threads, meetings } = stores;
  const location = useLocation();

  const recentCandidates = useMemo(
    () =>
      computed(() =>
        collectRecentCandidates(
          threads.visibleChats,
          meetings.meetingsByLastActivity
        )
      ),
    [threads, meetings]
  );
  const candidates = visible ? recentCandidates.get() : NO_CANDIDATES;
  const query = visible ? uiStore.commandBarQuery : '';

  const openThreadId = getThreadId({ location, query: '', stores });

  return useMemo(
    () =>
      visible && !query
        ? buildRecentActions(selectRecentItems(candidates, openThreadId))
        : NO_RECENT_ACTIONS,
    [visible, query, candidates, openThreadId]
  );
}
